import React from 'react';
import {
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    title: 'Opportunity Discovery',
    description: 'Find and match with federal, state, and local government contracts that fit your capabilities.',
    icon: MagnifyingGlassIcon,
    items: [
      'AI-powered contract matching',
      'Federal, state, and local opportunities',
      'Daily email alerts',
      'Market intelligence dashboard',
      'Competitor analysis',
    ],
  },
  {
    title: 'AI Workflow',
    description: 'Leverage AI to streamline your entire GovCon workflow from research to proposal submission.',
    icon: ChatBubbleLeftRightIcon,
    items: [
      'GovCon AI Chat - 24/7 assistant',
      'AI Document Hub for past performance',
      'RFP analysis and requirements extraction',
      'Compliance checking',
      'Win theme development',
    ],
  },
  {
    title: 'AI Proposal Writer',
    description: 'Generate compliant, compelling proposal sections in minutes instead of days.',
    icon: DocumentTextIcon,
    items: [
      'Technical approach generation',
      'Past performance narratives',
      'Management plan creation',
      'Compliance matrix generation',
      'Custom training on your win themes',
    ],
  },
  {
    title: 'Pipeline & CRM',
    description: 'Manage your entire GovCon pipeline from opportunity identification to contract award.',
    icon: ChartBarIcon,
    items: [
      'Custom pipeline stages',
      'Opportunity tracking',
      'Task management',
      'Deadline notifications',
      'Win rate analytics',
    ],
  },
  {
    title: 'Enterprise Security',
    description: 'Enterprise-grade security and compliance for government contractors.',
    icon: ShieldCheckIcon,
    items: [
      'FedRAMP authorization',
      'CMMC Level 2 compliance',
      'AES-256 encryption',
      'SSO (SAML) support',
      'Audit logs & access controls',
    ],
  },
  {
    title: 'Team Collaboration',
    description: 'Work together seamlessly with your capture and proposal teams.',
    icon: UsersIcon,
    items: [
      'Team workspaces',
      'Role-based permissions',
      'Real-time collaboration',
      'Comment and review system',
      'Activity feeds',
    ],
  },
];

const Features: React.FC = () => {
  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-brand-600">Features</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Everything you need to win GovCon contracts
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            From opportunity discovery to proposal submission, Clevor provides the tools and intelligence 
            you need to compete and win in the government contracting market.
          </p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2">
            {features.map((feature) => (
              <div key={feature.title} className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-gray-900">
                  <feature.icon className="h-6 w-6 flex-none text-brand-600" aria-hidden="true" />
                  {feature.title}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                  <ul className="mt-4 space-y-2">
                    {feature.items.map((item) => (
                      <li key={item} className="flex items-center">
                        <svg className="h-4 w-4 text-brand-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default Features;
